import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import MenuItem from '@mui/material/MenuItem';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useApi } from '../../../hooks/useApi';
import { usePageSize } from '../../../stores/settingsStore';

function useDebouncedValue(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function AdminUsers() {
  const navigate = useNavigate();
  const api = useApi();
  const [page, setPage] = useState(0);
  const { rowsPerPage, setRowsPerPage } = usePageSize('adminUsers');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isLoading, error, isError, refetch } = useQuery({
    queryKey: ['admin-users', page, rowsPerPage, debouncedSearch, roleFilter, orgFilter],
    queryFn: () => api.get('/api/admin/users', {
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch,
      ...(roleFilter && { role: roleFilter }),
      ...(orgFilter && { org: orgFilter }),
    }),
    placeholderData: (prev) => prev,
  });

  // Fetch distinct organizations for filter dropdown
  const { data: orgsData } = useQuery({
    queryKey: ['admin-user-orgs'],
    queryFn: () => api.get('/api/admin/users/filters/organizations'),
    staleTime: 60000,
  });

  const users = data?.users || [];
  const total = data?.pagination?.total || 0;
  const organizations = orgsData?.organizations || [];

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, roleFilter, orgFilter]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate('/admin')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">Users</Typography>
      </Box>
      <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search by name, email, or organization..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }
            }}
          />
          <TextField
            select
            label="Role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All Roles</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="tenant-owner">Tenant Owner</MenuItem>
            <MenuItem value="team-member">Team Member</MenuItem>
            <MenuItem value="org-owner">Org Owner</MenuItem>
            <MenuItem value="org-member">Org Member</MenuItem>
            <MenuItem value="hiyve-user">Hiyve User</MenuItem>
          </TextField>
          <TextField
            select
            label="Organization"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Organizations</MenuItem>
            {organizations.map((org) => (
              <MenuItem key={org} value={org}>{org}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', mb: 7 }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>API Key</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Roles</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Alert
                      severity="error"
                      action={
                        <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={() => refetch()}>
                          Retry
                        </Button>
                      }
                      sx={{ justifyContent: 'center' }}
                    >
                      {error?.message || 'Failed to load users'}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography sx={{
                      color: "text.secondary"
                    }}>No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user._id}
                    hover
                    onClick={() => navigate(`/admin/users/${user._id}`)}
                    sx={{
                      cursor: 'pointer',
                      opacity: user.active === false ? 0.5 : 1,
                      ...(user.roles?.includes('admin') && { bgcolor: 'rgba(211, 47, 47, 0.08)', borderLeft: '3px solid', borderLeftColor: 'error.main' }),
                      ...(!user.roles?.includes('admin') && user.roles?.includes('tenant-owner') && { bgcolor: 'rgba(25, 118, 210, 0.08)', borderLeft: '3px solid', borderLeftColor: 'primary.main' }),
                    }}
                  >
                    <TableCell>{user.name || '—'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {user.tenant || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 120, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {user.apiKey || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.active !== false ? 'Active' : 'Inactive'}
                        color={user.active !== false ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {[...new Set(user.roles)].map((role) => (
                          <Chip
                            key={role}
                            label={role}
                            size="small"
                            variant={role === 'admin' || role === 'tenant-owner' ? 'filled' : 'outlined'}
                            color={role === 'admin' ? 'error' : role === 'tenant-owner' ? 'primary' : 'default'}
                          />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1100,
        }}
      />
    </Box>
  );
}

export default AdminUsers;
